import type { PayloadAction, SerializedError } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import Bowser from 'bowser';
import {
  phoneStatusSet,
  phonesUnsubscribed,
} from '../../shared/reducers/contacts';
import { smsMessagesReset } from '../../shared/reducers/sms';
import { finaliseLogout } from '../../shared/store/thunks';
import { ExtensionStatus } from '../../shared/types/SipExtension';
import UA from '../JsSIP/UserAgent';
import { UserAgentAudio } from '../JsSIP/interfaces';
import { logActivity } from '../helpers/activityLog';
import appConfig from '../helpers/appConfig';
import type { ThunkAPI } from '../store/types';
import { callHistoryReset } from './callHistory';
import { incomingCall } from './calls';
import { subscribePhones } from './conatcts';

let storedAudio: UserAgentAudio;

export const getStoredAudio = () => storedAudio;

interface ExtensionState {
  username?: string;
  password?: string;
  status: ExtensionStatus;
  isUnregistering: boolean;
  error?: SerializedError;
}

const initialState: ExtensionState = {
  status: 'disconnected',
  isUnregistering: false,
  error: undefined,
};

export const registerExtension = createAsyncThunk<
  void,
  { username: string; password: string; domain: string; audio: UserAgentAudio },
  ThunkAPI
>('extension/registerExtension', (params, thunkAPI) => {
  const { username, password, domain, audio } = params;
  if (audio) {
    storedAudio = audio;
  }
  if (!username) {
    // If we've not got a username, try again in 1 second
    setTimeout(() => {
      logActivity('default', 'REGISTER_EXTENSION', {
        reason: 'REGISTER_RETRY',
      });
      thunkAPI.dispatch(
        registerExtension({ username, password, domain, audio }),
      );
    }, 1000);
    return;
  }

  // TODO_V2 there's probably a better way than pulling from store here
  const brandName = thunkAPI.getState().branding.brand;
  const prefix =
    process.env.NODE_ENV && process.env.NODE_ENV !== 'production'
      ? `[${process.env.NODE_ENV.substring(0, 3)}]`
      : '';
  const browser = Bowser.getParser(window.navigator.userAgent);
  const browserName = browser.getBrowserName();
  const browserVersion = browser.getBrowserVersion();
  const osName = browser.getOSName();
  const osVersion = browser.getOSVersion();

  const userAgent = `${prefix}${brandName} Communicator - ${browserName} ${browserVersion} on ${osName} ${osVersion}`;

  let instanceId = localStorage.getItem('@sipInstanceId');
  if (!instanceId) {
    instanceId = crypto.randomUUID();
    localStorage.setItem('@sipInstanceId', instanceId);
  }

  const config = {
    username,
    password,
    audio,
    userAgent,
    domain,
    instance_id: instanceId,
  };

  const extension = new UA(config);

  let reconnectionInterval: ReturnType<typeof setInterval> | undefined;

  extension.on('connecting', () => {
    thunkAPI.dispatch(extensionRegistering({ username, password }));
  });

  extension.on('registered', () => {
    // Stop trying to reconnect
    clearInterval(reconnectionInterval);
    reconnectionInterval = undefined;

    thunkAPI.dispatch(extensionRegistered());
    logActivity('default', 'EXTENSION_REGISTERED', {});
    // Once the extension has registered, store it in the app config
    appConfig.extension = extension;
    extension.clearSubscriptions();
    thunkAPI.dispatch(phonesUnsubscribed());
    thunkAPI.dispatch(subscribePhones());
  });

  extension.on('unregistered', () => {
    thunkAPI.dispatch(extensionUnregistered());
    thunkAPI.dispatch(callHistoryReset());
    thunkAPI.dispatch(smsMessagesReset());
    logActivity('default', 'EXTENSION_UNREGISTERED', {});

    if (extension.isActive) {
      // Start trying to reconnect
      reconnectionInterval = setInterval(() => {
        console.debug('Attempting to reconnect');
        extension.connect();
      }, 10000);
    }
  });

  extension.on('disconnected', () => {
    if (!extension.isRegistered()) {
      return;
    }
    thunkAPI.dispatch(extensionDisconnected());
  });

  extension.on('registrationFailed', () => {
    thunkAPI.dispatch(extensionRegistrationFailed());
  });

  extension.on('incomingCall' as any, (call: any): void => {
    thunkAPI.dispatch(incomingCall(call));
  });

  extension.on('userStateChanged' as any, (user: any, state: any) => {
    thunkAPI.dispatch(phoneStatusSet({ id: user, status: state }));
  });

  extension.connect();

  appConfig.extension = extension;
});

const extensionSlice = createSlice({
  name: 'extension',
  initialState,
  reducers: {
    extensionUnregistering: (state) => {
      state.isUnregistering = true;
    },
    extensionRegistering: (
      state,
      action: PayloadAction<{ username: string; password: string }>,
    ) => {
      state.status = 'registering';
      state.username = action.payload.username;
      state.password = action.payload.password;
    },

    extensionRegistered: (state) => {
      state.status = 'registered';
    },

    extensionUnregistered: (state) => {
      state.username = '';
      state.password = '';
      state.status = 'disconnected';
    },
    extensionRegistrationFailed: (state) => {
      state.status = 'registrationFailed';
    },
    extensionDisconnected: (state) => {
      state.status = 'disconnected';
    },
  },
  extraReducers: (builder) => {
    builder.addCase(finaliseLogout.fulfilled, () => initialState);
  },
});

export const {
  extensionUnregistering,
  extensionUnregistered,
  extensionRegistering,
  extensionRegistered,
  extensionRegistrationFailed,
  extensionDisconnected,
} = extensionSlice.actions;

export default extensionSlice.reducer;
